import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings as SettingsIcon, Globe, Moon, Shield, Database, Zap, Check, ArrowLeft } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { cn } from '../lib/utils';
import { Network } from '../types';
import { useNavigate } from 'react-router-dom';

export const Settings: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const [saveStatus, setSaveStatus] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const handleRpcChange = (network: string, value: string) => {
    updateSettings({
      rpcEndpoints: {
        ...settings.rpcEndpoints,
        [network]: value
      }
    });
    showSaveStatus('RPC settings updated');
  };

  const showSaveStatus = (msg: string) => {
    setSaveStatus(msg);
    setTimeout(() => setSaveStatus(null), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-20 px-8">
      <div className="max-w-4xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors mb-12 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Back to Terminal
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 relative"
        >
          <div className="flex items-center gap-3 mb-6">
            <SettingsIcon className="w-8 h-8 text-emerald-500" />
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-white uppercase">Settings</h1>
          </div>
          <p className="text-[#525252] font-mono text-lg leading-relaxed">
            Configure your Oru Trace environment and preferences.
          </p>

          <AnimatePresence>
            {saveStatus && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="absolute top-0 right-0 flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full"
              >
                <Check className="w-4 h-4 text-emerald-500" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">{saveStatus}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <div className="space-y-8">
          <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-8 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              RPC Endpoints
            </h3>
            <div className="space-y-6">
              {[
                { network: "ethereum", label: "Ethereum Mainnet", placeholder: "https://eth-mainnet.g.alchemy.com/v2/..." },
                { network: "arbitrum", label: "Arbitrum One", placeholder: "https://arb-mainnet.g.alchemy.com/v2/..." },
                { network: "optimism", label: "Optimism", placeholder: "https://opt-mainnet.g.alchemy.com/v2/..." }
              ].map((rpc) => (
                <div key={rpc.network} className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#404040]">{rpc.label}</label>
                  <input 
                    type="text" 
                    value={settings.rpcEndpoints[rpc.network] || ''}
                    onChange={(e) => handleRpcChange(rpc.network, e.target.value)}
                    placeholder={rpc.placeholder} 
                    className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-emerald-500/50 transition-all shadow-inner shadow-black/20"
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-8 flex items-center gap-2">
              <Moon className="w-4 h-4" />
              Appearance
            </h3>
            <div className="flex items-center justify-between p-4 rounded-xl bg-[#1A1A1A] border border-[#262626]">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold text-white uppercase tracking-tight">Dark Mode</span>
                <span className="text-[10px] font-mono text-[#525252]">
                  {settings.darkMode ? "Optimized for precision debugging" : "Standard visual profile"}
                </span>
              </div>
              <button 
                onClick={() => updateSettings({ darkMode: !settings.darkMode })}
                className={cn(
                  "w-12 h-6 rounded-full transition-all relative flex items-center px-1",
                  settings.darkMode ? "bg-emerald-500" : "bg-[#262626]"
                )}
              >
                <div className={cn(
                  "w-4 h-4 rounded-full bg-black transition-transform",
                  settings.darkMode ? "ml-auto" : "ml-0"
                )} />
              </button>
            </div>
          </section>

          <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-8 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Privacy & Security
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-[#1A1A1A] border border-[#262626]">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-bold text-white uppercase tracking-tight">Telemetry</span>
                  <span className="text-[10px] font-mono text-[#525252]">Help us improve Oru Trace with anonymous usage data</span>
                </div>
                <button 
                  onClick={() => updateSettings({ telemetry: !settings.telemetry })}
                  className={cn(
                    "w-12 h-6 rounded-full transition-all relative flex items-center px-1",
                    settings.telemetry ? "bg-emerald-500" : "bg-[#262626]"
                  )}
                >
                  <div className={cn(
                    "w-4 h-4 rounded-full bg-black transition-transform",
                    settings.telemetry ? "ml-auto" : "ml-0"
                  )} />
                </button>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-[#1A1A1A] border border-[#262626]">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-bold text-white uppercase tracking-tight">Cache Duration</span>
                  <span className="text-[10px] font-mono text-[#525252]">How long to store trace data locally</span>
                </div>
                <select 
                  value={settings.cacheDuration}
                  onChange={(e) => updateSettings({ cacheDuration: e.target.value as any })}
                  className="bg-[#0A0A0A] border border-[#262626] rounded px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white focus:outline-none focus:border-emerald-500/50"
                >
                  <option value="24h">24 Hours</option>
                  <option value="7d">7 Days</option>
                  <option value="30d">30 Days</option>
                </select>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
